import {
  Badge,
  Button,
  ColumnHint,
  Card,
  CardContent,
  ErrorState,
  PageHeader,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  Upload,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  cancelImport,
  commitImport,
  createBuildingImport,
  fetchImport,
} from '../../../api/buildings.js';
import { uploadFile } from '../../../api/uploads.js';
import { ApiError } from '../../../lib/api-client.js';

import type { ImportIssue, ImportJobSummary, ImportPreviewResult } from '@sitewise/shared';

const STEPS = [
  { id: 'upload', label: 'Upload', description: 'Pick a CSV or XLSX file.' },
  { id: 'preview', label: 'Preview', description: 'Review the parser + geocoder output.' },
  { id: 'commit', label: 'Commit', description: 'Persist the buildings and units.' },
];

function stepIndexForStatus(status: string | undefined): number {
  switch (status) {
    case undefined:
      return 0;
    case 'pending':
    case 'previewing':
      return 1;
    case 'previewed':
      return 1;
    case 'committing':
      return 2;
    case 'completed':
      return STEPS.length;
    case 'failed':
    case 'cancelled':
      return 2;
    default:
      return 0;
  }
}

export function ImportWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: routeId } = useParams<{ id?: string }>();
  const [createdImportId, setCreatedImportId] = useState<string | null>(routeId ?? null);

  useEffect(() => {
    if (routeId) setCreatedImportId(routeId);
  }, [routeId]);

  const importQuery = useQuery({
    queryKey: ['import', createdImportId],
    queryFn: () => fetchImport(createdImportId!),
    enabled: Boolean(createdImportId),
    refetchInterval: (q) => {
      const job = q.state.data?.import;
      if (!job) return 1000;
      const terminal = ['completed', 'failed', 'cancelled'];
      const previewing = ['pending', 'previewing', 'committing'];
      if (terminal.includes(job.status)) return false;
      if (previewing.includes(job.status)) return 1500;
      return 5000;
    },
  });

  const job = importQuery.data?.import;
  const stepIndex = stepIndexForStatus(job?.status);

  return (
    <>
      <PageHeader
        title="Bulk import buildings"
        description="Upload a CSV or XLSX; SiteWise validates, geocodes, and previews before any data is written."
        breadcrumbs={[
          { label: 'Buildings', href: '/buildings' },
          { label: 'Import' },
        ]}
        action={
          <Link to="/buildings">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" /> Cancel
            </Button>
          </Link>
        }
      />

      <div className="mb-6">
        <Stepper steps={STEPS} currentIndex={stepIndex} />
      </div>

      {!createdImportId ? (
        <UploadStep
          onUploaded={(importId) => {
            setCreatedImportId(importId);
            void queryClient.invalidateQueries({ queryKey: ['imports'] });
          }}
        />
      ) : null}

      {createdImportId && job ? (
        <PreviewAndCommit
          job={job}
          onCancelled={() => {
            setCreatedImportId(null);
            navigate('/buildings/import');
          }}
          onCompleted={() => navigate('/buildings')}
        />
      ) : null}
    </>
  );
}

function UploadStep({ onUploaded }: { onUploaded: (importId: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const kind: 'building_import_csv' | 'building_import_xlsx' =
        file.name.toLowerCase().endsWith('.xlsx')
          ? 'building_import_xlsx'
          : 'building_import_csv';
      const { uploadId } = await uploadFile(file, kind);
      const result = await createBuildingImport({ uploadId, label: file.name });
      return result.import.id;
    },
    onSuccess: (importId) => onUploaded(importId),
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Upload failed');
    },
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="mt-1 h-6 w-6 text-fg-muted" />
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Drop in your spreadsheet</h2>
            <p className="text-sm text-fg-secondary">
              Required columns: <code className="rounded bg-surface-muted px-1.5 py-0.5">name</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">address_line_1</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">city</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">state</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">postal_code</code>. Optional:{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">bin</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">bbl</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">units</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">notes</code>.
            </p>
            <p className="text-sm text-fg-secondary">
              Shelter format is also supported for CSV/XLSX uploads: columns{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Facility Name</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Building Name</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Provider Name</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Building Borough</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Facility Type</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Unit Name</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Unit / Bed Capacity</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Bed Type Crib</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Bed Type Bunk</code>,{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Bed Type Twin</code>, and{' '}
              <code className="rounded bg-surface-muted px-1.5 py-0.5">Bed Type Full</code>. Capacity
              is validated using <code className="rounded bg-surface-muted px-1.5 py-0.5">I + (J * 2) + K + L</code>.
            </p>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setError(null);
              uploadMutation.mutate(file);
            }
          }}
        />

        <div className="flex items-center gap-2">
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploadMutation.isPending}
          >
            <Upload className="h-4 w-4" />
            {uploadMutation.isPending ? 'Uploading…' : 'Choose file'}
          </Button>
          <a
            href="/templates/buildings.csv"
            download="buildings-import-template.csv"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Download className="h-3.5 w-3.5" /> Download template
          </a>
        </div>

        {error ? <ErrorState description={error} /> : null}
      </CardContent>
    </Card>
  );
}

interface PreviewProps {
  job: ImportJobSummary;
  onCancelled: () => void;
  onCompleted: () => void;
}

function PreviewAndCommit({ job, onCancelled, onCompleted }: PreviewProps) {
  const queryClient = useQueryClient();
  const [ackWarnings, setAckWarnings] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [showAllSample, setShowAllSample] = useState(false);

  const commitMutation = useMutation({
    mutationFn: () => commitImport(job.id, ackWarnings),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['import', job.id] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        const details = err.details as { reason?: string; warningCount?: number } | null;
        if (details?.reason === 'WARNINGS_REQUIRE_ACK') {
          setCommitError(
            `${details.warningCount ?? 0} warnings need to be acknowledged before committing.`,
          );
          return;
        }
      }
      setCommitError(err instanceof Error ? err.message : 'Commit failed');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelImport(job.id),
    onSuccess: onCancelled,
  });

  useEffect(() => {
    if (job.status === 'completed') {
      const t = setTimeout(onCompleted, 2_500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [job.status, onCompleted]);

  if (job.status === 'pending' || job.status === 'previewing') {
    return (
      <Card>
        <CardContent className="space-y-2 p-6 text-sm text-fg-secondary">
          <p className="font-semibold text-fg">Parsing and geocoding…</p>
          <p>
            We&apos;re reading the file, validating each row, and asking NYC GeoSearch to resolve
            BIN/BBL. This usually takes a few seconds.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (job.status === 'failed') {
    return (
      <ErrorState
        title="Import failed"
        description={job.failureReason ?? 'See errors below for details.'}
        action={
          <Link to="/buildings/import">
            <Button onClick={onCancelled}>Try again</Button>
          </Link>
        }
      />
    );
  }

  if (job.status === 'cancelled') {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-fg-secondary">
          Import was cancelled. Start a new one above.
        </CardContent>
      </Card>
    );
  }

  if (job.status === 'committing') {
    return (
      <Card>
        <CardContent className="space-y-2 p-6 text-sm text-fg-secondary">
          <p className="font-semibold text-fg">Inserting buildings and units…</p>
          <p>This page will update once the commit finishes.</p>
        </CardContent>
      </Card>
    );
  }

  if (job.status === 'completed') {
    return (
      <Card>
        <CardContent className="space-y-2 p-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-success" aria-hidden />
          <p className="text-base font-semibold">Import complete</p>
          <p className="text-sm text-fg-secondary">
            Added <strong>{job.committedRows}</strong> buildings and{' '}
            <strong>{job.unitsCreated}</strong> units. Returning to your portfolio…
          </p>
        </CardContent>
      </Card>
    );
  }

  // status === 'previewed'
  const preview = job.preview as ImportPreviewResult | null;
  const sample = preview?.sample ?? [];
  const errors = preview?.errors ?? [];
  const warnings = preview?.warnings ?? [];
  const visible = showAllSample ? sample : sample.slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-4">
        <CounterCard label="Rows" value={job.totalRows} />
        <CounterCard label="Valid" value={job.validRows} tone="success" />
        <CounterCard label="Skipped" value={job.skippedRows} tone="warning" />
        <CounterCard label="Errors" value={job.errorRows} tone="danger" />
      </div>

      {errors.length > 0 ? (
        <IssuePanel title="Errors" issues={errors} tone="danger" />
      ) : null}
      {warnings.length > 0 ? (
        <IssuePanel title="Warnings" issues={warnings} tone="warning" />
      ) : null}

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Preview</h3>
              <p className="text-xs text-fg-muted">
                Sampling the first {Math.min(sample.length, visible.length)} of {sample.length} rows.
              </p>
            </div>
            {sample.length > 10 ? (
              <Button variant="outline" size="sm" onClick={() => setShowAllSample((v) => !v)}>
                {showAllSample ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" /> Show all
                  </>
                )}
              </Button>
            ) : null}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <ColumnHint description="Row number in the uploaded file.">#</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="The building name from that row.">Building</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="Street address that will be geocoded.">Address</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="New York City building identifiers, when the file or geocoder provided them.">
                    BIN / BBL
                  </ColumnHint>
                </TableHead>
                <TableHead className="w-20">
                  <ColumnHint description="How many units this row will create.">Units</ColumnHint>
                </TableHead>
                <TableHead className="w-24">
                  <ColumnHint description="Whether the row will be imported or skipped.">Status</ColumnHint>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((row) => (
                <TableRow key={`row-${row.row}`} className={row.willSkip ? 'opacity-60' : ''}>
                  <TableCell className="text-fg-muted tabular-nums">{row.row}</TableCell>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-fg-secondary">
                    {row.addressLine1}, {row.city}, {row.state} {row.postalCode}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums text-fg-secondary">
                    {row.bin ? `BIN ${row.bin}` : '—'}
                    {row.bbl ? <div>BBL {row.bbl}</div> : null}
                  </TableCell>
                  <TableCell className="tabular-nums">{row.unitsToCreate}</TableCell>
                  <TableCell>
                    {row.willSkip ? (
                      <Badge variant="warning">Skip</Badge>
                    ) : row.geocoded ? (
                      <Badge variant="success">Geocoded</Badge>
                    ) : (
                      <Badge variant="outline">No match</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-fg-secondary">
          {warnings.length > 0 ? (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={ackWarnings}
                onChange={(e) => setAckWarnings(e.target.checked)}
              />
              I&apos;ve reviewed the {warnings.length} warnings above.
            </label>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => cancelMutation.mutate()}>
            Cancel import
          </Button>
          <Button
            onClick={() => {
              setCommitError(null);
              commitMutation.mutate();
            }}
            disabled={
              commitMutation.isPending ||
              job.errorRows > 0 ||
              (warnings.length > 0 && !ackWarnings)
            }
          >
            {commitMutation.isPending
              ? 'Committing…'
              : `Commit ${job.validRows.toLocaleString()} buildings`}
          </Button>
        </div>
      </div>

      {commitError ? <ErrorState description={commitError} /> : null}

      {job.errorRows > 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-subtle px-4 py-3 text-sm text-fg">
          <XCircle className="mt-0.5 h-4 w-4 text-danger" aria-hidden />
          <span>
            Fix the {job.errorRows} row errors in your spreadsheet and re-upload the file before
            committing.
          </span>
        </div>
      ) : null}
    </div>
  );
}

function CounterCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warning' | 'danger';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-fg';
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</p>
        <p className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
          {value.toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}

function IssuePanel({
  title,
  issues,
  tone,
}: {
  title: string;
  issues: ImportIssue[];
  tone: 'danger' | 'warning';
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, ImportIssue[]>();
    for (const issue of issues) {
      const key = issue.code;
      const list = map.get(key) ?? [];
      list.push(issue);
      map.set(key, list);
    }
    return map;
  }, [issues]);

  return (
    <Card
      className={
        tone === 'danger'
          ? 'border-danger/30 bg-danger-subtle'
          : 'border-warning/30 bg-warning-subtle'
      }
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-fg">
            {title} ({issues.length})
          </h3>
        </div>
        <ul className="space-y-1.5 text-sm">
          {[...grouped.entries()].map(([code, list]) => (
            <li key={code} className="text-fg-secondary">
              <span className="font-medium text-fg">{code}</span> ({list.length}):{' '}
              <span className="text-fg-muted">
                rows {list.slice(0, 8).map((i) => i.row).join(', ')}
                {list.length > 8 ? `, +${list.length - 8} more` : ''}
              </span>
              <div className="text-xs text-fg-muted">{list[0]?.message}</div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
